import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  MessageSquare, 
  Scale, 
  TrendingUp, 
  Sparkles,
  ArrowRight,
  Calculator,
  FileText,
  PiggyBank,
  ChevronRight,
  BarChart3,
  Clock,
  AlertTriangle,
  Heart,
  Menu,
  X,
  ExternalLink,
  GraduationCap,
  Trash2,
  Share2,
  MoreVertical,
  History,
  IndianRupee,
  Zap,
  Download,
  BookOpen,
  Crown,
  Mail
} from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Bell } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import ThemeToggle from "../components/ThemeToggle";
import FeedbackModal from "../components/FeedbackModal";
import NewsletterSubscribe from "../components/NewsletterSubscribe";
import LatestUpdatesModal from "../components/LatestUpdatesModal";
import NewsTicker from "../components/NewsTicker";

const LandingPage = () => {
  const navigate = useNavigate();
  const { user, initializeOneTap } = useAuth();
  const [isVisible, setIsVisible] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [canStartVideo, setCanStartVideo] = useState(false);
  const [recentChats, setRecentChats] = useState([]);
  const [chatMenuOpen, setChatMenuOpen] = useState(null);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [isLatestUpdatesOpen, setIsLatestUpdatesOpen] = useState(false);
  const videoRef = useRef(null);

  // Belated Return deadline countdown — 31 December 2026, 23:59:59 IST = 18:30:00 UTC
  const BELATED_DEADLINE_MS = new Date("2026-12-31T18:30:00Z").getTime();
  const computeDaysLeft = () => Math.max(0, Math.ceil((BELATED_DEADLINE_MS - Date.now()) / (1000 * 60 * 60 * 24)));
  const [itrDaysLeft, setItrDaysLeft] = useState(computeDaysLeft);
  useEffect(() => {
    const id = setInterval(() => setItrDaysLeft(computeDaysLeft()), 60 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  // Initialize Google One-Tap on landing page for non-logged-in users
  useEffect(() => {
    if (!user) {
      // Small delay to let the page load first
      const timer = setTimeout(() => {
        initializeOneTap?.();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [user, initializeOneTap]);

  // Smart API URL
  const getApiBaseUrl = () => {
    const envUrl = process.env.REACT_APP_BACKEND_URL;
    const currentOrigin = window.location.origin;
    if (!currentOrigin.includes('preview.emergentagent.com') && 
        !currentOrigin.includes('emergent.host')) {
      return currentOrigin;
    }
    return envUrl;
  };
  const BACKEND_URL = getApiBaseUrl();

  // Animated Logo Video URL
  const TAXSETU_LOGO_VIDEO = "https://customer-assets.emergentagent.com/job_78cf9280-da7a-4b52-a25e-41321aa4bb13/artifacts/uc08umjk_openart-video_7c1eefe1_1768140331696.mp4";

  // Ticker messages for scrolling banner
  const tickerMessages = [
    "🤖 AI-Powered Tax Assistant",
    "📋 Instant IT Notice Analysis",
    "💰 80GGC/80GGB Donation Calculator",
    "📊 ITA 1961 vs 2025 Comparison",
    "🧮 Old vs New Regime Calculator",
    "🔍 Section-wise Tax Guide",
    "💬 Ask in English or Hinglish",
    "⚡ Get Answers in Seconds"
  ];

  useEffect(() => {
    setIsVisible(true);
    
    // Wait 1.5 seconds before allowing video to play
    const timer = setTimeout(() => {
      setCanStartVideo(true);
    }, 1500);
    
    // Fetch recent chats
    fetchRecentChats();
    
    return () => clearTimeout(timer);
  }, [user?.email]); // Refetch when user email changes

  // Fetch recent chat sessions - filter by user email if logged in
  const fetchRecentChats = async () => {
    try {
      const url = user?.email 
        ? `${BACKEND_URL}/api/chat-sessions?user_email=${encodeURIComponent(user.email)}`
        : `${BACKEND_URL}/api/chat-sessions`;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setRecentChats(data.sessions || []);
      }
    } catch (error) {
      console.log('Could not fetch recent chats');
    }
  };

  // Delete a chat session
  const deleteChat = async (sessionId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/chat-session/${sessionId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        setRecentChats(prev => prev.filter(chat => chat.session_id !== sessionId));
      }
    } catch (error) {
      console.log('Could not delete chat');
    }
    setChatMenuOpen(null);
  };

  // Share a chat (copy link)
  const shareChat = (sessionId) => {
    const shareUrl = `${window.location.origin}/chat?session=${sessionId}`;
    navigator.clipboard.writeText(shareUrl);
    setChatMenuOpen(null);
    // Could add a toast notification here
  };

  // Try to play video when it's ready AND we've waited enough time
  useEffect(() => {
    if (canStartVideo && videoRef.current) {
      videoRef.current.play()
        .then(() => setVideoPlaying(true))
        .catch((err) => console.log('Video autoplay failed:', err));
    }
  }, [canStartVideo]);

  // Handle video end
  const handleVideoEnd = () => {
    setVideoPlaying(false);
  };

  // Featured/Prominent features first
  const features = [
    {
      icon: <AlertTriangle className="w-6 h-6" />,
      title: "Analyze IT Notice",
      description: "Upload your IT notice and get AI analysis with response drafts. Our most powerful feature!",
      color: "red",
      link: "/notice",
      featured: true
    },
    {
      icon: <Heart className="w-6 h-6" />,
      title: "Political Donations",
      description: "Check 80GGC/80GGB eligibility and calculate tax benefits for political contributions.",
      color: "pink",
      link: "/donations",
      featured: true
    },
    {
      icon: <MessageSquare className="w-6 h-6" />,
      title: "AI Tax Assistant",
      description: "Ask anything about Income Tax in English or Hinglish. Get instant, accurate answers.",
      color: "cyan",
      link: "/chat"
    },
    {
      icon: <Scale className="w-6 h-6" />,
      title: "IT Section Mapper",
      description: "Map sections of ITA 1961 to corresponding clauses in ITA 2025.",
      color: "amber",
      link: "/section-mapper"
    },
    {
      icon: <Calculator className="w-6 h-6" />,
      title: "Tax Calculator",
      description: "Calculate your tax under both regimes and find which one saves more.",
      color: "cyan",
      link: "/calculator"
    },
    {
      icon: <FileText className="w-6 h-6" />,
      title: "Section-wise Guide",
      description: "Deep dive into specific sections - 80C, 80D, HRA, Capital Gains & more.",
      color: "amber",
      link: "/section-mapper"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  // TaxSetu Logo URL (transparent background)
  const TAXSETU_LOGO = "https://customer-assets.emergentagent.com/job_78cf9280-da7a-4b52-a25e-41321aa4bb13/artifacts/xl6xxrnp_TaxSetu_Only_Logo_Design_Infinite-removebg-preview.png";

  return (
    <div className="min-h-screen mesh-gradient">
      {/* Sidebar Drawer */}
      <AnimatePresence>
        {isSidebarOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
              onClick={() => setIsSidebarOpen(false)}
            />
            
            {/* Sidebar */}
            <motion.div
              initial={{ x: -320 }}
              animate={{ x: 0 }}
              exit={{ x: -320 }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="fixed left-0 top-0 h-full w-80 bg-slate-900 border-r border-slate-800 z-50 overflow-y-auto"
            >
              {/* Sidebar Header */}
              <div className="p-4 border-b border-slate-800 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <img src={TAXSETU_LOGO} alt="TaxSetu" className="w-8 h-8 object-contain" />
                  <span className="font-heading font-semibold text-white">TaxSetu AI</span>
                </div>
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => setIsSidebarOpen(false)}
                  className="text-slate-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Quick Actions */}
              <div className="p-4">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Quick Actions</h3>
                <div className="space-y-1">
                  <button
                    onClick={() => { navigate('/notice'); setIsSidebarOpen(false); }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
                  >
                    <AlertTriangle className="w-5 h-5 text-red-400" />
                    <span>Analyze Tax Notice</span>
                  </button>
                  <button
                    onClick={() => { navigate('/donations'); setIsSidebarOpen(false); }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
                  >
                    <Heart className="w-5 h-5 text-pink-400" />
                    <span>Political Donations</span>
                  </button>
                  <button
                    onClick={() => { navigate('/donations'); setIsSidebarOpen(false); }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
                  >
                    <FileText className="w-5 h-5 text-cyan-400" />
                    <span>Political Health Check</span>
                  </button>
                  <button
                    onClick={() => { navigate('/chat'); setIsSidebarOpen(false); }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
                  >
                    <MessageSquare className="w-5 h-5 text-amber-400" />
                    <span>AI Tax Chat</span>
                  </button>
                  <button
                    onClick={() => { setIsFeedbackOpen(true); setIsSidebarOpen(false); }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
                    data-testid="sidebar-feedback-btn"
                  >
                    <MessageSquare className="w-5 h-5 text-pink-400" />
                    <span>Share Feedback</span>
                  </button>
                </div>
              </div>

              {/* Advance Tax Calculator — featured card */}
              <div className="px-4 pb-3">
                <button
                  onClick={() => { navigate('/advance-tax'); setIsSidebarOpen(false); }}
                  className="w-full p-3 rounded-lg bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border border-emerald-500/30 hover:border-emerald-400/60 transition-all text-left"
                  data-testid="sidebar-advance-tax-btn"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Calculator className="w-4 h-4 text-emerald-400" />
                    <span className="font-semibold text-emerald-600 dark:text-white text-sm">Advance Tax</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500 text-white font-bold animate-pulse">NEW</span>
                  </div>
                  <p className="text-xs text-slate-600 dark:text-slate-400">AY 2026-27 quarterly schedule & safe-harbour check</p>
                </button>
              </div>

              {/* Tax Audit — Clause 20(b) — featured card */}
              <div className="px-4 pb-3">
                <button
                  onClick={() => { navigate('/tax-audit/clause-20b'); setIsSidebarOpen(false); }}
                  className="w-full p-3 rounded-lg bg-gradient-to-r from-fuchsia-500/10 to-purple-500/10 border border-fuchsia-500/30 hover:border-fuchsia-400/60 transition-all text-left"
                  data-testid="sidebar-tax-audit-clause20b-btn"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <FileText className="w-4 h-4 text-fuchsia-400" />
                    <span className="font-semibold text-fuchsia-600 dark:text-white text-sm">Tax Audit · 20(b)</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-fuchsia-500 text-white font-bold animate-pulse">NEW</span>
                  </div>
                  <p className="text-xs text-slate-600 dark:text-slate-400">PF/ESI challans → 3CD Excel annexure in 30 sec</p>
                </button>
              </div>

              {/* Latest Updates — featured card */}
              <div className="px-4 pb-3">
                <button
                  onClick={() => { setIsLatestUpdatesOpen(true); setIsSidebarOpen(false); }}
                  className="w-full p-3 rounded-lg bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 hover:border-cyan-400/60 transition-all text-left"
                  data-testid="sidebar-latest-updates-btn"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Bell className="w-4 h-4 text-cyan-400" />
                    <span className="font-semibold text-cyan-600 dark:text-white text-sm">Latest Updates</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-cyan-500 text-white font-bold animate-pulse">1</span>
                  </div>
                  <p className="text-xs text-slate-600 dark:text-slate-400">Circulars, notifications & CBDT alerts</p>
                </button>
              </div>

              {/* Budget 2026 - Navigate to Chat */}
              <div className="px-4 pb-4">
                <button
                  onClick={() => { navigate('/chat'); setIsSidebarOpen(false); }}
                  className="w-full p-3 rounded-lg bg-gradient-to-r from-amber-500/10 to-cyan-500/10 border border-amber-500/20 hover:border-amber-500/40 transition-all text-left"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <IndianRupee className="w-4 h-4 text-amber-400" />
                    <span className="font-medium text-amber-600 dark:text-white text-sm">Budget 2026</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500 text-white font-bold animate-pulse">NEW</span>
                  </div>
                  <p className="text-xs text-slate-600 dark:text-slate-400">Analyze Budget 2026 impact on your taxes</p>
                </button>
              </div>

              {/* Divider */}
              <div className="border-t border-slate-800 mx-4"></div>

              {/* Recent Chats */}
              <div className="p-4">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <History className="w-4 h-4" />
                  Recent Chats
                </h3>
                <div className="space-y-1">
                  {recentChats.length === 0 ? (
                    <p className="text-sm text-slate-500 py-2">No recent chats</p>
                  ) : (
                    recentChats.slice(0, 10).map((chat) => (
                      <div
                        key={chat.session_id}
                        className="group flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-800 transition-colors"
                      >
                        <MessageSquare className="w-4 h-4 text-slate-500 flex-shrink-0" />
                        <button
                          onClick={() => { navigate(`/chat?session=${chat.session_id}`); setIsSidebarOpen(false); }}
                          className="flex-1 text-left text-sm text-slate-300 truncate hover:text-white"
                        >
                          {chat.preview || 'New Chat'}
                        </button>
                        <div className="relative">
                          <button
                            onClick={(e) => { e.stopPropagation(); setChatMenuOpen(chatMenuOpen === chat.session_id ? null : chat.session_id); }}
                            className="p-1 opacity-0 group-hover:opacity-100 text-slate-400 hover:text-white transition-opacity"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>
                          
                          {/* Chat Options Menu */}
                          {chatMenuOpen === chat.session_id && (
                            <div className="absolute right-0 top-6 w-36 bg-slate-800 rounded-lg shadow-lg border border-slate-700 py-1 z-10">
                              <button
                                onClick={() => shareChat(chat.session_id)}
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-slate-700"
                              >
                                <Share2 className="w-4 h-4" />
                                Share
                              </button>
                              <button
                                onClick={() => deleteChat(chat.session_id)}
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-slate-700"
                              >
                                <Trash2 className="w-4 h-4" />
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* External Links */}
              <div className="p-4 border-t border-slate-800">
                <a
                  href="https://practiq.taxsetu.ai/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-slate-300 hover:bg-slate-800 hover:text-amber-400 transition-colors"
                >
                  <GraduationCap className="w-5 h-5" />
                  <span>PractiQ</span>
                  <ExternalLink className="w-3 h-3 ml-auto" />
                </a>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Feedback Modal */}
      <FeedbackModal 
        isOpen={isFeedbackOpen} 
        onClose={() => setIsFeedbackOpen(false)}
        currentPage="landing"
      />

      {/* Latest Updates & Explore Modal */}
      <LatestUpdatesModal
        isOpen={isLatestUpdatesOpen}
        onClose={() => setIsLatestUpdatesOpen(false)}
        onNavigate={(path) => {
          if (path.startsWith("/")) navigate(path);
        }}
      />

      {/* Navigation - Full Width */}
      <nav className="fixed top-0 left-0 right-0 z-40 glass">
        <div className="w-full px-3 sm:px-6 xl:px-8 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            {/* Hamburger Menu Button — All screens */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsSidebarOpen(true)}
              className="flex text-slate-400 hover:text-white h-9 w-9 sm:h-10 sm:w-10"
              data-testid="sidebar-toggle"
              aria-label="Open menu"
            >
              <Menu className="w-5 h-5" />
            </Button>
            
            <div className="flex items-center gap-2 cursor-pointer group" onClick={() => navigate('/')}>
              <img 
                src={TAXSETU_LOGO} 
                alt="TaxSetu Logo" 
                className="w-9 h-9 object-contain"
              />
              <span className="text-xl font-bold font-heading text-white group-hover:text-cyan-400 transition-colors whitespace-nowrap">TaxSetu AI</span>
            </div>
          </div>
          
          {/* Desktop Navigation - Simplified: Latest Updates / PractiQ / Feedback / Advance Tax / Start Chat */}
          <div className="hidden 2xl:flex items-center gap-1 flex-shrink-0">
            <Button 
              variant="ghost" 
              size="sm"
              className="relative text-cyan-300 hover:text-cyan-200 font-semibold text-sm px-3"
              onClick={() => setIsLatestUpdatesOpen(true)}
              data-testid="nav-latest-updates-btn"
            >
              <Bell className="w-4 h-4 mr-1.5" />
              Latest Updates
              <span className="absolute -top-1 -right-1 px-1.5 py-0.5 text-[9px] font-bold bg-cyan-500 text-white rounded-full animate-pulse">1</span>
            </Button>
            <a
              href="https://practiq.taxsetu.ai/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-slate-300 hover:text-amber-400 px-3 py-2 transition-colors text-sm"
            >
              <GraduationCap className="w-4 h-4" />
              PractiQ
              <ExternalLink className="w-3 h-3" />
            </a>
            <Button 
              variant="ghost"
              size="sm"
              className="text-pink-400 hover:text-pink-300 text-sm px-3"
              onClick={() => setIsFeedbackOpen(true)}
              data-testid="nav-feedback-btn"
            >
              <MessageSquare className="w-4 h-4 mr-1" />
              Feedback
            </Button>
            <Button 
              variant="ghost"
              size="sm"
              className="relative text-emerald-400 hover:text-emerald-300 font-semibold text-sm px-3"
              onClick={() => navigate('/advance-tax')}
              data-testid="nav-advance-tax-btn"
            >
              <Calculator className="w-4 h-4 mr-1.5" />
              Advance Tax Calculator
              <span className="absolute -top-1 -right-1 px-1.5 py-0.5 text-[9px] font-bold bg-emerald-500 text-white rounded-full animate-pulse">NEW</span>
            </Button>
            <div className="mx-2"><ThemeToggle /></div>
            <Button 
              size="sm"
              className="btn-primary text-white font-semibold px-5 text-sm ml-2"
              onClick={() => navigate('/chat')}
              data-testid="nav-chat-btn"
            >
              Start Chat
              <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </div>
          
          {/* Mobile/Tablet/Small Desktop Quick Actions - Visible on screens < 1536px */}
          <div className="flex 2xl:hidden items-center gap-2 sm:gap-3">
            <ThemeToggle />
            <Button 
              size="sm"
              variant="outline"
              className="border-2 border-emerald-400/60 dark:border-emerald-500/60 text-emerald-600 dark:text-emerald-300 hover:bg-emerald-500/10 hover:border-emerald-500 dark:hover:border-emerald-400 font-medium px-3 sm:px-4 text-xs sm:text-sm"
              onClick={() => document.getElementById('newsletter')?.scrollIntoView({ behavior: 'smooth' })}
              data-testid="nav-subscribe-btn-mobile"
            >
              <Mail className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">Subscribe</span>
            </Button>
            <Button 
              size="sm"
              className="btn-primary text-white font-medium px-3 sm:px-5 text-xs sm:text-sm"
              onClick={() => navigate('/chat')}
            >
              <span className="hidden sm:inline">Start Chat</span>
              <span className="sm:hidden">Chat</span>
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="text-white"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu - Shows on screens < 1536px */}
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="2xl:hidden glass border-t border-slate-800 px-6 py-4 space-y-3"
          >
            {/* BUDGET 2026 - HIGHLIGHT in mobile menu */}
            <Button 
              variant="ghost" 
              className="w-full justify-start text-amber-400 hover:text-amber-300 font-semibold relative"
              onClick={() => { navigate('/chat'); setIsMobileMenuOpen(false); }}
            >
              <Zap className="w-4 h-4 mr-2 animate-pulse" />
              Budget 2026
              <span className="ml-2 px-1.5 py-0.5 text-[10px] font-bold bg-red-500 text-white rounded-full animate-bounce">
                NEW
              </span>
            </Button>
            <Button 
              variant="ghost" 
              className="w-full justify-start text-emerald-400 hover:text-emerald-300 font-semibold relative"
              onClick={() => { navigate('/advance-tax'); setIsMobileMenuOpen(false); }}
              data-testid="mobile-nav-advance-tax-btn"
            >
              <Calculator className="w-4 h-4 mr-2" />
              Advance Tax Calculator
              <span className="ml-2 px-1.5 py-0.5 text-[10px] font-bold bg-emerald-500 text-white rounded-full animate-pulse">
                NEW
              </span>
            </Button>
            <Button 
              variant="ghost" 
              className="w-full justify-start text-cyan-300 hover:text-cyan-200 font-semibold relative"
              onClick={() => { setIsLatestUpdatesOpen(true); setIsMobileMenuOpen(false); }}
              data-testid="mobile-nav-latest-updates-btn"
            >
              <Bell className="w-4 h-4 mr-2" />
              Latest Updates
              <span className="ml-2 px-1.5 py-0.5 text-[10px] font-bold bg-cyan-500 text-white rounded-full animate-pulse">
                1
              </span>
            </Button>
            <Button 
              variant="ghost" 
              className="w-full justify-start text-slate-300 hover:text-white"
              onClick={() => { navigate('/chat'); setIsMobileMenuOpen(false); }}
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              AI Chat
            </Button>
            <Button 
              variant="ghost" 
              className="w-full justify-start text-slate-300 hover:text-white"
              onClick={() => { navigate('/notice'); setIsMobileMenuOpen(false); }}
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              Analyze IT Notice
            </Button>
            <Button 
              variant="ghost" 
              className="w-full justify-start text-slate-300 hover:text-white"
              onClick={() => { navigate('/donations'); setIsMobileMenuOpen(false); }}
            >
              <Heart className="w-4 h-4 mr-2" />
              Political Donations
            </Button>
            <Button 
              variant="ghost" 
              className="w-full justify-start text-slate-300 hover:text-white"
              onClick={() => { navigate('/section-mapper'); setIsMobileMenuOpen(false); }}
            >
              <Scale className="w-4 h-4 mr-2" />
              Section Mapper
            </Button>
            <Button 
              variant="ghost" 
              className="w-full justify-start text-slate-300 hover:text-white"
              onClick={() => { navigate('/calculator'); setIsMobileMenuOpen(false); }}
            >
              <Calculator className="w-4 h-4 mr-2" />
              Calculator
            </Button>
            <a
              href="https://practiq.taxsetu.ai/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-amber-400 hover:text-amber-300 px-3 py-2 transition-colors"
            >
              <GraduationCap className="w-4 h-4" />
              PractiQ
              <ExternalLink className="w-3 h-3" />
            </a>
            <Button 
              variant="ghost" 
              className="w-full justify-start text-pink-400 hover:text-pink-300"
              onClick={() => { setIsFeedbackOpen(true); setIsMobileMenuOpen(false); }}
              data-testid="mobile-nav-feedback-btn"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Feedback
            </Button>
            {/* Theme Toggle in Mobile Menu */}
            <div className="flex items-center justify-between px-3 py-2 border-t border-slate-700 mt-2 pt-3">
              <span className="text-slate-400 text-sm">Theme</span>
              <ThemeToggle />
            </div>
          </motion.div>
        )}
      </nav>

      {/* Live regulatory news ticker — sits below the nav with a small breathing gap */}
      <div className="fixed left-0 right-0 z-30 top-[64px] sm:top-[72px]">
        <NewsTicker />
      </div>

      {/* Hero Section - Full Width */}
      <section className="pt-32 sm:pt-40 lg:pt-44 pb-10 px-4 sm:px-6 lg:px-12">
        <div className="w-full">
          <motion.div 
            className="text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
            transition={{ duration: 0.6 }}
          >
            {/* Animated Logo */}
            <div className="flex justify-center mb-6">
              <div className="relative w-24 h-24 sm:w-28 sm:h-28 lg:w-32 lg:h-32">
                {/* Static logo - visible until video starts playing */}
                <img
                  src={TAXSETU_LOGO}
                  alt="TaxSetu Logo"
                  className="w-full h-full object-contain absolute inset-0 z-10 transition-opacity duration-300"
                  style={{ opacity: videoPlaying ? 0 : 1 }}
                />
                
                {/* Video - preloads in background, plays after 1.5s delay */}
                <video
                  ref={videoRef}
                  src={TAXSETU_LOGO_VIDEO}
                  className="w-full h-full object-contain absolute inset-0"
                  muted
                  playsInline
                  preload="auto"
                  onEnded={handleVideoEnd}
                  style={{ 
                    mixBlendMode: 'lighten',
                    opacity: videoPlaying ? 1 : 0
                  }}
                />
              </div>
            </div>

            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass mb-6">
              <Sparkles className="w-4 h-4 text-amber-500" />
              <span className="text-sm text-slate-300">Powered by Advanced AI</span>
              <span className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-gradient-to-r from-cyan-500/20 to-amber-500/20 text-cyan-400 rounded-full border border-cyan-500/30">Beta</span>
            </div>
            
            {/* Main Headline — scales down to keep one line on mobile */}
            <h1 className="text-[6.8vw] sm:text-5xl lg:text-6xl xl:text-7xl font-bold font-heading tracking-tight mb-4 whitespace-nowrap">
              <span className="text-white">Namaste, I am </span>
              <span className="gradient-text">TaxSetu AI</span>
            </h1>
            
            {/* Brand Tagline - Highlighted */}
            <div className="mb-6">
              <span className="text-xl sm:text-2xl lg:text-3xl font-heading font-semibold text-[#2D3748] dark:bg-clip-text dark:text-transparent dark:bg-gradient-to-r dark:from-cyan-400 dark:via-cyan-300 dark:to-amber-400">
                Your AI Tax Expert for India
              </span>
            </div>
            
            {/* Description - Triple Layer Analysis USP */}
            <p className="text-base md:text-lg text-slate-600 dark:text-slate-400 mb-3 max-w-3xl mx-auto leading-relaxed">
              India's first AI covering <span className="text-slate-800 dark:text-white font-semibold">Income Tax</span> & <span className="text-slate-800 dark:text-white font-semibold">GST</span> — with 
              <span className="text-cyan-600 dark:text-cyan-400"> Income Tax Act 2025</span>, 
              <span className="text-amber-600 dark:text-amber-400"> IT Act 1961</span>, 
              <span className="text-pink-600 dark:text-pink-400"> Finance Act, 2026</span> & 
              <span className="text-emerald-600 dark:text-emerald-400"> GST Acts</span>.
            </p>
            <p className="text-sm text-emerald-600 dark:text-emerald-400/90 mb-4 max-w-2xl mx-auto font-medium">
              Now with <span className="text-emerald-700 dark:text-emerald-300">Income Tax Rules, 2026</span> + Complete <span className="text-cyan-700 dark:text-cyan-300">GST Compliance</span>
            </p>
            <p className="text-sm md:text-base text-slate-500 mb-6 max-w-2xl mx-auto">
              Get instant, accurate answers in <span className="text-cyan-600 dark:text-cyan-400">English</span> or <span className="text-amber-600 dark:text-amber-400">Hinglish</span>.
            </p>

            {/* Social Proof Strip — Trust Signal */}
            <div
              className="inline-flex flex-wrap items-center justify-center gap-x-3 gap-y-2 px-5 py-2.5 mb-8 rounded-full bg-white/[0.04] border border-white/10 backdrop-blur-sm shadow-sm"
              data-testid="hero-social-proof"
            >
              <span className="inline-flex items-center gap-1.5">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70 animate-ping" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                </span>
                <span className="text-[15px] sm:text-base font-extrabold text-white tracking-tight">3,300+</span>
                <span className="text-[12px] sm:text-[13px] text-slate-300 font-medium">Queries Resolved</span>
              </span>
              <span className="hidden sm:inline-block w-px h-4 bg-white/20" />
              <span className="inline-flex items-center gap-1.5 text-[12px] sm:text-[13px] text-slate-300 font-medium">
                <Sparkles className="w-3.5 h-3.5 text-amber-400" />
                Trusted by Taxpayers Across India
              </span>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-10">
              <Button 
                size="lg"
                className="btn-primary text-white font-bold px-12 py-7 text-xl rounded-full shadow-xl shadow-cyan-500/40 hover:shadow-cyan-500/60 hover:scale-105 transition-all"
                onClick={() => navigate('/chat')}
                data-testid="hero-start-chat-btn"
              >
                <MessageSquare className="w-7 h-7 mr-3" />
                Ask TaxSetu AI
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="border-2 border-slate-400 dark:border-slate-500 text-slate-700 dark:text-white hover:bg-slate-100 dark:hover:bg-slate-800 hover:border-slate-500 dark:hover:border-slate-400 px-12 py-7 text-xl rounded-full hover:scale-105 transition-all"
                onClick={() => navigate('/calculator')}
                data-testid="hero-calculator-btn"
              >
                <Calculator className="w-7 h-7 mr-3" />
                Tax Calculator
              </Button>
            </div>

            {/* ITR Filing Hero CTA — Season Lead Magnet (with live countdown) */}
            <div className="max-w-5xl mx-auto mb-8">
              <div className="relative overflow-hidden rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-900/40 via-cyan-900/30 to-emerald-800/40 backdrop-blur-sm px-6 sm:px-8 lg:px-10 py-7 sm:py-8 shadow-xl shadow-emerald-900/20">
                <div className="pointer-events-none absolute -top-20 -right-20 w-64 h-64 rounded-full bg-emerald-500/15 blur-3xl" />
                <div className="pointer-events-none absolute -bottom-20 -left-20 w-64 h-64 rounded-full bg-cyan-500/10 blur-3xl" />

                <div className="relative grid grid-cols-1 lg:grid-cols-[1.35fr_1fr] gap-6 lg:gap-10 items-stretch">

                  {/* LEFT — Headline + subtitle + CTAs */}
                  <div className="text-center lg:text-left flex flex-col">
                    <div className="flex flex-wrap items-center justify-center lg:justify-start gap-2 mb-4">
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-500/20 text-emerald-300 text-[10px] font-bold uppercase tracking-wider border border-emerald-500/30">
                        <Sparkles className="w-3 h-3" /> Return Not Filed?
                      </span>
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-[10.5px] text-slate-300 font-semibold tracking-wide">
                        AY 2026-27 · FY 2025-26
                      </span>
                      <span
                        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-500/20 text-red-200 text-[11px] font-bold border border-red-500/40 shadow-sm shadow-red-500/20"
                        data-testid="itr-countdown-pill"
                      >
                        <Clock className="w-3 h-3 animate-pulse" />
                        {itrDaysLeft > 0
                          ? <>Only <span className="text-white font-extrabold">{itrDaysLeft} days</span> left for Belated Return</>
                          : <>Belated window closed</>}
                      </span>
                    </div>

                    <h3 className="text-2xl sm:text-[28px] lg:text-[32px] font-bold font-heading text-white leading-[1.15] tracking-tight">
                      File Belated / Revised
                      <br className="hidden lg:block" />
                      <span className="lg:inline"> ITR for AY 2026-27</span>{" "}
                      <span className="text-emerald-300 text-[80%] font-semibold whitespace-nowrap">(FY 2025-26)</span>
                    </h3>

                    <p className="text-[13.5px] sm:text-sm text-slate-300/95 mt-3 leading-relaxed max-w-xl mx-auto lg:mx-0">
                      Missed 31 Jul? File a Belated Return before 31 Dec 2026 — or Revise your filed return till 31 Mar 2027.
                    </p>

                    <div className="flex flex-col sm:flex-row items-stretch justify-center lg:justify-start gap-3 sm:gap-4 mt-6">
                      <button
                        type="button"
                        onClick={() => navigate('/chat')}
                        data-testid="itr-hero-chat-ai-btn"
                        className="group inline-flex items-center justify-center gap-2 h-14 px-7 min-w-[200px] rounded-xl text-base font-bold text-white bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-600 hover:to-emerald-600 shadow-lg shadow-cyan-500/30 hover:shadow-cyan-500/50 hover:scale-[1.03] active:scale-[0.98] transition-all"
                      >
                        <MessageSquare className="w-5 h-5" />
                        <span>Chat with AI</span>
                      </button>
                      <a
                        href="https://wa.me/919711162342?text=Hi%20TaxSetu%20AI%2C%20I%20need%20to%20file%20my%20ITR.%20Please%20help."
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="itr-hero-talk-ca-btn"
                        className="group inline-flex items-center justify-center gap-2 h-14 px-7 min-w-[200px] rounded-xl text-base font-bold text-white bg-[#25D366] hover:bg-[#1ebe5a] shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/50 hover:scale-[1.03] active:scale-[0.98] transition-all"
                      >
                        <svg viewBox="0 0 32 32" className="w-5 h-5 fill-white" aria-hidden="true">
                          <path d="M19.11 17.41c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.27-.47-2.42-1.49-.89-.79-1.5-1.77-1.67-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51l-.57-.01c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.49 0 1.47 1.07 2.89 1.22 3.09.15.2 2.1 3.2 5.08 4.49.71.31 1.27.5 1.7.64.71.23 1.36.2 1.87.12.57-.08 1.76-.72 2.01-1.41.25-.69.25-1.28.17-1.41-.07-.13-.27-.2-.57-.35zM16.06 4.65c-6.27 0-11.36 5.09-11.36 11.36 0 2.01.53 3.95 1.53 5.66l-1.63 5.94 6.09-1.6c1.66.91 3.53 1.39 5.36 1.39 6.27 0 11.36-5.09 11.36-11.36 0-3.03-1.18-5.89-3.32-8.03-2.14-2.14-5-3.32-8.03-3.32zm0 20.83c-1.66 0-3.29-.45-4.71-1.29l-.34-.2-3.51.92.94-3.42-.22-.35a9.45 9.45 0 0 1-1.45-5.03c0-5.22 4.25-9.47 9.47-9.47 2.53 0 4.91.99 6.7 2.77 1.79 1.79 2.77 4.17 2.77 6.7 0 5.22-4.25 9.47-9.47 9.47z" />
                        </svg>
                        <span>Talk to Expert</span>
                      </a>
                    </div>
                  </div>

                  {/* RIGHT — Deadline / trust panel */}
                  <div className="relative lg:border-l lg:border-white/10 lg:pl-8 flex flex-col justify-center">
                    <div className="text-[10.5px] font-bold uppercase tracking-wider text-emerald-300/80 mb-3 text-center lg:text-left">
                      Important Deadlines
                    </div>
                    <div className="space-y-2.5">
                      <div className="flex items-center justify-between gap-3 px-3.5 py-2.5 rounded-xl bg-white/[0.04] border border-white/10">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-amber-500/15 border border-amber-400/30 flex items-center justify-center">
                            <Clock className="w-4 h-4 text-amber-300" />
                          </span>
                          <div className="text-left min-w-0">
                            <div className="text-[12.5px] font-bold text-white leading-tight">Belated Return</div>
                            <div className="text-[10.5px] text-slate-400 leading-tight">Section 139(4) · with late fee</div>
                          </div>
                        </div>
                        <div className="text-[12px] font-bold text-amber-300 whitespace-nowrap">31 Dec 2026</div>
                      </div>
                      <div className="flex items-center justify-between gap-3 px-3.5 py-2.5 rounded-xl bg-white/[0.04] border border-white/10">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-orange-500/15 border border-orange-400/30 flex items-center justify-center">
                            <Clock className="w-4 h-4 text-orange-300" />
                          </span>
                          <div className="text-left min-w-0">
                            <div className="text-[12.5px] font-bold text-white leading-tight">Revised Return</div>
                            <div className="text-[10.5px] text-slate-400 leading-tight">Section 139(5) · correct errors</div>
                          </div>
                        </div>
                        <div className="text-[12px] font-bold text-orange-300 whitespace-nowrap">31 Mar 2027</div>
                      </div>
                      <div className="flex items-center justify-between gap-3 px-3.5 py-2.5 rounded-xl bg-emerald-500/[0.07] border border-emerald-500/25">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center">
                            <Sparkles className="w-4 h-4 text-emerald-300" />
                          </span>
                          <div className="text-left min-w-0">
                            <div className="text-[12.5px] font-bold text-white leading-tight">First chat is Free</div>
                            <div className="text-[10.5px] text-slate-400 leading-tight">AI · available 24×7</div>
                          </div>
                        </div>
                        <span className="text-[10px] font-bold text-emerald-300 uppercase tracking-wider whitespace-nowrap">₹0</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Labour Codes 2026 CTA — Mobile-first prominent card */}
            <div className="max-w-2xl mx-auto mb-10">
              <button
                type="button"
                onClick={() => navigate('/labour-law')}
                data-testid="hero-labour-codes-btn"
                className="group w-full relative overflow-hidden rounded-2xl bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 hover:from-amber-600 hover:via-orange-600 hover:to-red-600 text-white font-bold shadow-xl shadow-orange-500/30 hover:shadow-orange-500/50 hover:scale-[1.02] active:scale-[0.99] transition-all border-0 text-left"
              >
                <span className="flex items-center gap-3 px-4 sm:px-6 py-3.5 sm:py-4">
                  <span className="flex-shrink-0 w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-white/15 flex items-center justify-center backdrop-blur-sm">
                    <Scale className="w-5 h-5 sm:w-6 sm:h-6" />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="flex items-center gap-2 flex-wrap">
                      <span className="text-[15px] sm:text-lg font-bold leading-tight">Labour Codes 2026</span>
                      <span className="px-1.5 py-0.5 bg-white/25 rounded text-[9px] font-extrabold tracking-wider animate-pulse">NEW</span>
                    </span>
                    <span className="block text-[11.5px] sm:text-sm text-white/90 mt-0.5 leading-snug">
                      Are you ready for the new labour regime?
                    </span>
                  </span>
                  <ArrowRight className="flex-shrink-0 w-5 h-5 opacity-90 group-hover:translate-x-1 transition-transform" />
                </span>
              </button>
              <p className="text-center text-[11px] sm:text-xs text-slate-400 mt-2 px-2 leading-snug">
                4 New Labour Codes effective from 21 Nov 2025 · 50% Wage Rule · 48-Hour F&amp;F Settlement
              </p>
            </div>

            {/* Feature Ticker - Subtle showcase of capabilities */}
            <div className="max-w-4xl mx-auto overflow-hidden rounded-full glass border border-slate-700/50">
              <div className="py-2">
                <div className="ticker-scroll flex whitespace-nowrap">
                  {[...tickerMessages, ...tickerMessages, ...tickerMessages].map((msg, idx) => (
                    <span key={idx} className="mx-6 text-xs text-slate-400">
                      {msg}
                    </span>
                  ))}
                </div>
              </div>
              <style>{`
                @keyframes ticker {
                  0% { transform: translateX(0); }
                  100% { transform: translateX(-33.33%); }
                }
                .ticker-scroll {
                  animation: ticker 25s linear infinite;
                }
                .ticker-scroll:hover {
                  animation-play-state: paused;
                }
              `}</style>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Specialized Tax Tools - IT Notice & Donations - TOP SECTION */}
      <section id="featured-tools" className="py-10 px-6 lg:px-12 scroll-mt-24" style={{ scrollMarginTop: '100px' }}>
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-white mb-2">
              Specialized <span className="gradient-text">Tax Tools</span>
            </h2>
            <p className="text-slate-400 text-sm md:text-base">
              Unique features you won't find anywhere else
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Analyze IT Notice - Featured */}
            <motion.div
              className="glass rounded-2xl p-6 lg:p-8 border border-red-500/30 cursor-pointer hover:border-red-500/60 transition-all group"
              whileHover={{ y: -5, scale: 1.02 }}
              onClick={() => navigate('/notice')}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-xl bg-red-500/20 flex items-center justify-center text-red-400 flex-shrink-0 group-hover:bg-red-500/30 transition-colors">
                  <AlertTriangle className="w-7 h-7" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-xl font-bold font-heading text-white">Analyze IT Notice</h3>
                    <span className="text-xs px-2 py-1 rounded-full bg-red-500/20 text-red-400 animate-pulse">Popular</span>
                  </div>
                  <p className="text-slate-400 text-sm mb-4">
                    Got an Income Tax notice? Upload it and get instant AI analysis with professional response drafts.
                  </p>
                  <Button className="bg-red-500/20 text-red-400 hover:bg-red-500/30 border-0">
                    Analyze Notice
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </motion.div>

            {/* Political Donations - Featured */}
            <motion.div
              className="glass rounded-2xl p-6 lg:p-8 border border-pink-500/30 cursor-pointer hover:border-pink-500/60 transition-all group"
              whileHover={{ y: -5, scale: 1.02 }}
              onClick={() => navigate('/donations')}
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-xl bg-pink-500/20 flex items-center justify-center text-pink-400 flex-shrink-0 group-hover:bg-pink-500/30 transition-colors">
                  <Heart className="w-7 h-7" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-xl font-bold font-heading text-white">Political Donations</h3>
                    <span className="text-xs px-2 py-1 rounded-full bg-pink-500/20 text-pink-400">Unique</span>
                  </div>
                  <p className="text-slate-400 text-sm mb-4">
                    Check 80GGC/80GGB eligibility. Verify party status with our database of 2,267+ parties.
                  </p>
                  <Button className="bg-pink-500/20 text-pink-400 hover:bg-pink-500/30 border-0">
                    Check Eligibility
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Resources Section - IT Rules, Budget, PDFs */}
      <section className="py-10 px-6 lg:px-12 bg-gradient-to-b from-transparent via-slate-900/30 to-transparent">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-[#2D3748] dark:text-white mb-2">
              Tax <span className="gradient-text">Resources</span>
            </h2>
            <p className="text-slate-600 dark:text-slate-400 text-sm md:text-base">
              Latest updates and official documents integrated into TaxSetu AI
            </p>
          </motion.div>
          
          {/* Resources Grid - Premium Glassmorphism Design */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            
            {/* Income Tax Rules 2026 */}
            <motion.div 
              className="relative overflow-hidden rounded-2xl cursor-pointer transition-all duration-300 group
                bg-white/70 dark:bg-slate-900/60 
                backdrop-blur-xl 
                border border-emerald-300/40 dark:border-emerald-500/30 
                shadow-lg shadow-emerald-500/5
                hover:shadow-2xl hover:shadow-emerald-500/20 
                hover:border-emerald-400/60 dark:hover:border-emerald-500/50
                hover:scale-[1.02]"
              onClick={() => navigate('/chat')}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              whileHover={{ y: -8 }}
            >
              {/* Gradient overlay for depth */}
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/50 via-transparent to-teal-50/30 dark:from-emerald-500/5 dark:via-transparent dark:to-transparent opacity-60 group-hover:opacity-100 transition-opacity" />
              
              <div className="relative p-6">
                <div className="flex items-start gap-3 mb-3">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500 dark:from-emerald-500/20 dark:to-teal-500/20 flex items-center justify-center border border-emerald-400/50 dark:border-emerald-500/30 shadow-lg shadow-emerald-500/30">
                    <BookOpen className="w-6 h-6 text-white dark:text-emerald-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-emerald-500 text-white rounded-full animate-pulse">
                        New
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-[#2D3748] dark:text-white">
                      IT Rules 2026
                    </h3>
                  </div>
                </div>
                
                <p className="text-sm text-[#4A5568] dark:text-slate-400 mb-3">
                  Notified rules for IT Act 2025. Motor Car, RFA, HRA & 60+ provisions.
                </p>
                
                <div className="flex flex-wrap gap-1.5">
                  <span className="px-2 py-0.5 text-[10px] rounded-full bg-slate-100 dark:bg-slate-800/80 text-[#4A5568] dark:text-slate-400 border border-slate-200 dark:border-slate-700/50">
                    Perquisites
                  </span>
                  <span className="px-2 py-0.5 text-[10px] rounded-full bg-emerald-100 dark:bg-slate-800/80 text-emerald-600 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-500/30">
                    Integrated
                  </span>
                </div>
              </div>
            </motion.div>
            
            {/* Finance Act, 2026 */}
            <motion.div 
              className="relative overflow-hidden rounded-2xl cursor-pointer transition-all duration-300 group
                bg-white/70 dark:bg-slate-900/60 
                backdrop-blur-xl 
                border border-amber-300/40 dark:border-amber-500/30 
                shadow-lg shadow-amber-500/5
                hover:shadow-2xl hover:shadow-amber-500/20 
                hover:border-amber-400/60 dark:hover:border-amber-500/50
                hover:scale-[1.02]"
              onClick={() => navigate('/finance-bill')}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              whileHover={{ y: -8 }}
            >
              {/* Gradient overlay for depth */}
              <div className="absolute inset-0 bg-gradient-to-br from-amber-50/50 via-transparent to-yellow-50/30 dark:from-amber-500/5 dark:via-transparent dark:to-transparent opacity-60 group-hover:opacity-100 transition-opacity" />
              
              <div className="relative p-6">
                <div className="flex items-start gap-3 mb-3">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-yellow-500 dark:from-amber-500/20 dark:to-yellow-500/20 flex items-center justify-center border border-amber-400/50 dark:border-amber-500/30 shadow-lg shadow-amber-500/30">
                    <Zap className="w-6 h-6 text-white dark:text-amber-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-amber-500 text-white rounded-full">
                        PDF
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-[#2D3748] dark:text-white">
                      Finance Act, 2026
                    </h3>
                  </div>
                </div>
                
                <p className="text-sm text-[#4A5568] dark:text-slate-400 mb-3">
                  Download Memo & Full Text. 8 Key Changes - TCS slashed, Due dates extended & more.
                </p>
                
                <div className="flex flex-wrap gap-1.5">
                  <span className="px-2 py-0.5 text-[10px] rounded-full bg-slate-100 dark:bg-slate-800/80 text-[#4A5568] dark:text-slate-400 border border-slate-200 dark:border-slate-700/50">
                    Download PDF
                  </span>
                  <span className="px-2 py-0.5 text-[10px] rounded-full bg-amber-100 dark:bg-slate-800/80 text-amber-600 dark:text-amber-400 border border-amber-300 dark:border-amber-500/30">
                    Key Changes
                  </span>
                </div>
              </div>
            </motion.div>
            
            {/* Budget 2026 PDF */}
            <motion.div 
              className="relative overflow-hidden rounded-2xl cursor-pointer transition-all duration-300 group
                bg-white/70 dark:bg-slate-900/60 
                backdrop-blur-xl 
                border border-cyan-300/40 dark:border-cyan-500/30 
                shadow-lg shadow-cyan-500/5
                hover:shadow-2xl hover:shadow-cyan-500/20 
                hover:border-cyan-400/60 dark:hover:border-cyan-500/50
                hover:scale-[1.02]"
              onClick={() => window.open('https://www.indiabudget.gov.in/', '_blank')}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              whileHover={{ y: -8 }}
            >
              {/* Gradient overlay for depth */}
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-50/50 via-transparent to-teal-50/30 dark:from-cyan-500/5 dark:via-transparent dark:to-transparent opacity-60 group-hover:opacity-100 transition-opacity" />
              
              <div className="relative p-6">
                <div className="flex items-start gap-3 mb-3">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-400 to-teal-500 dark:from-cyan-500/20 dark:to-teal-500/20 flex items-center justify-center border border-cyan-400/50 dark:border-cyan-500/30 shadow-lg shadow-cyan-500/30">
                    <Download className="w-6 h-6 text-white dark:text-cyan-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-cyan-500 text-white rounded-full">
                        PDF
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-[#2D3748] dark:text-white">
                      Budget 2026 Docs
                    </h3>
                  </div>
                </div>
                
                <p className="text-sm text-[#4A5568] dark:text-slate-400 mb-3">
                  Official Union Budget documents, memorandum & finance bill from India Budget portal.
                </p>
                
                <div className="flex flex-wrap gap-1.5">
                  <span className="px-2 py-0.5 text-[10px] rounded-full bg-slate-100 dark:bg-slate-800/80 text-[#4A5568] dark:text-slate-400 border border-slate-200 dark:border-slate-700/50">
                    Official
                  </span>
                  <span className="px-2 py-0.5 text-[10px] rounded-full bg-cyan-100 dark:bg-slate-800/80 text-cyan-600 dark:text-cyan-400 border border-cyan-300 dark:border-cyan-500/30">
                    External Link
                  </span>
                </div>
              </div>
            </motion.div>
            
          </div>
          
          {/* Ask CTA */}
          <div className="mt-8 text-center">
            <Button 
              size="lg"
              className="bg-gradient-to-r from-[#24B4B9] to-[#1D2B53] hover:from-[#2EC4C9] hover:to-[#253663] text-white font-semibold px-10 py-4 rounded-full shadow-xl shadow-[#24B4B9]/30 hover:shadow-2xl hover:shadow-[#24B4B9]/40 transition-all duration-300 hover:scale-105"
              onClick={() => navigate('/chat')}
            >
              Ask TaxSetu AI About Updates
              <ArrowRight className="w-5 h-5 ml-2 text-white" />
            </Button>
          </div>
        </div>
      </section>

      {/* What is TaxSetu AI - Clear Intent Section */}
      <section id="who-we-are" className="py-12 px-6 lg:px-12 bg-gradient-to-b from-transparent via-slate-900/50 to-transparent scroll-mt-20">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="text-3xl sm:text-4xl font-bold font-heading text-white mb-4">
              What is <span className="gradient-text">TaxSetu AI</span>?
            </h2>
            <p className="text-lg text-slate-400 max-w-3xl mx-auto">
              TaxSetu AI is India's first AI-powered Income Tax expert that helps individuals, CAs, and tax professionals 
              navigate complex tax laws instantly. No more searching through PDFs or waiting for consultations.
            </p>
          </motion.div>

          {/* Value Proposition Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="glass rounded-2xl p-6 text-center border border-cyan-500/20 hover:border-cyan-500/40 transition-all"
            >
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-cyan-500/20 flex items-center justify-center">
                <MessageSquare className="w-8 h-8 text-cyan-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Ask Anything</h3>
              <p className="text-slate-400 text-sm">
                Ask tax questions in English or Hinglish. Get accurate answers based on Income Tax Act 1961 & 2025.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="glass rounded-2xl p-6 text-center border border-amber-500/20 hover:border-amber-500/40 transition-all"
            >
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-500/20 flex items-center justify-center">
                <Clock className="w-8 h-8 text-amber-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Instant Answers</h3>
              <p className="text-slate-400 text-sm">
                Get responses in seconds, not hours. No waiting for callbacks or searching through documents.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="glass rounded-2xl p-6 text-center border border-pink-500/20 hover:border-pink-500/40 transition-all"
            >
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-pink-500/20 flex items-center justify-center">
                <Scale className="w-8 h-8 text-pink-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Expert Accuracy</h3>
              <p className="text-slate-400 text-sm">
                Trained on complete Income Tax Act with section references. Responses a CA would be proud of.
              </p>
            </motion.div>
          </div>

          {/* Who is it for */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass rounded-2xl p-8 border border-slate-700"
          >
            <h3 className="text-2xl font-bold text-white mb-6 text-center">Who is TaxSetu AI for?</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-800/50">
                <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                  <IndianRupee className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <p className="font-semibold text-white text-sm">Individual Taxpayers</p>
                  <p className="text-xs text-slate-400">Filing ITR, claiming deductions</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-800/50">
                <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                  <GraduationCap className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="font-semibold text-white text-sm">CA Students</p>
                  <p className="text-xs text-slate-400">Learning tax provisions</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-800/50">
                <div className="w-10 h-10 rounded-full bg-pink-500/20 flex items-center justify-center flex-shrink-0">
                  <BarChart3 className="w-5 h-5 text-pink-400" />
                </div>
                <div>
                  <p className="font-semibold text-white text-sm">Tax Professionals</p>
                  <p className="text-xs text-slate-400">Quick reference & research</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-800/50">
                <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="font-semibold text-white text-sm">Business Owners</p>
                  <p className="text-xs text-slate-400">Tax planning & compliance</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Budget 2026 Resources Section */}
      <section className="py-12 px-6 lg:px-12 bg-gradient-to-b from-transparent via-amber-900/10 to-transparent">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/30 mb-4">
              <Zap className="w-4 h-4 text-amber-400" />
              <span className="text-sm text-amber-400 font-semibold">Budget 2026 Resources</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold font-heading text-white mb-4">
              Finance Act, 2026 - <span className="text-amber-400">Download & Learn</span>
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Get the official documents and understand the key changes that affect you
            </p>
          </motion.div>

          {/* PDF Download Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
            <motion.a
              href="https://customer-assets.emergentagent.com/job_taxsetu-ai-1/artifacts/b5ioz90b_Memo%20Explaining%20Finance%20Bill%202026.pdf"
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass rounded-2xl p-6 border border-amber-500/30 hover:border-amber-500/60 transition-all group cursor-pointer"
            >
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-xl bg-amber-500/20 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <BookOpen className="w-7 h-7 text-amber-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-white mb-2 group-hover:text-amber-400 transition-colors">
                    Memo Explaining Finance Act, 2026
                  </h3>
                  <p className="text-slate-400 text-sm mb-3">
                    Simplified explanation of all changes - perfect for understanding the "why" behind each amendment
                  </p>
                  <div className="flex items-center gap-2 text-amber-400 text-sm font-medium">
                    <Download className="w-4 h-4" />
                    Download PDF
                    <ExternalLink className="w-3 h-3" />
                  </div>
                </div>
              </div>
            </motion.a>

            <motion.a
              href="https://customer-assets.emergentagent.com/job_taxsetu-ai-1/artifacts/psq9o116_Finance_Bill%202026.pdf"
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass rounded-2xl p-6 border border-cyan-500/30 hover:border-cyan-500/60 transition-all group cursor-pointer"
            >
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-xl bg-cyan-500/20 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <FileText className="w-7 h-7 text-cyan-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-white mb-2 group-hover:text-cyan-400 transition-colors">
                    Finance Act, 2026 - Full Text
                  </h3>
                  <p className="text-slate-400 text-sm mb-3">
                    Complete Finance Bill as presented in Parliament - for detailed legal reference
                  </p>
                  <div className="flex items-center gap-2 text-cyan-400 text-sm font-medium">
                    <Download className="w-4 h-4" />
                    Download PDF
                    <ExternalLink className="w-3 h-3" />
                  </div>
                </div>
              </div>
            </motion.a>
          </div>

          {/* Key Highlights Quick View */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass rounded-2xl p-6 border border-slate-700"
          >
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-400" />
              8 Key Changes You Must Know
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { icon: "📅", title: "Due Date Extended", desc: "July 31 → Aug 31 for non-audit" },
                { icon: "📝", title: "Revised Return", desc: "9 → 12 months period" },
                { icon: "🌍", title: "FAST-DS 2026", desc: "Foreign Assets disclosure scheme" },
                { icon: "📉", title: "Loss Reduction", desc: "ITR-U now allowed for loss" },
                { icon: "📬", title: "After 148 Notice", desc: "ITR-U allowed with 10% tax" },
                { icon: "💸", title: "TCS Slashed", desc: "5%/20% → 2% on LRS" },
                { icon: "📈", title: "Buyback Tax", desc: "Dividend → Capital Gains" },
                { icon: "⚖️", title: "Decriminalized", desc: "≤₹10L = Fine only" }
              ].map((item, idx) => (
                <div key={idx} className="p-3 rounded-lg bg-slate-800/50 hover:bg-slate-800 transition-colors">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">{item.icon}</span>
                    <span className="text-sm font-semibold text-white">{item.title}</span>
                  </div>
                  <p className="text-xs text-slate-400">{item.desc}</p>
                </div>
              ))}
            </div>
            <div className="mt-4 text-center">
              <Button 
                className="bg-gradient-to-r from-[#24B4B9] to-[#1D2B53] hover:from-[#2EC4C9] hover:to-[#253663] text-white font-semibold px-8 py-3 rounded-full shadow-xl shadow-[#24B4B9]/30 hover:shadow-2xl hover:shadow-[#24B4B9]/40 transition-all duration-300 hover:scale-105"
                onClick={() => navigate('/chat')}
              >
                <MessageSquare className="w-4 h-4 mr-2 text-white" />
                Ask TaxSetu AI About Budget 2026
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Other Features Section - Full Width */}
      <section className="py-16 px-6 lg:px-12">
        <div className="w-full">
          <motion.div
            className="text-center mb-10"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold font-heading text-white mb-4">
              Everything you need for
              <span className="gradient-text"> Tax Planning</span>
            </h2>
            <p className="text-slate-400 text-base md:text-lg">
              From understanding basic concepts to comparing complex provisions - TaxSetu has you covered.
            </p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {features.filter(f => !f.featured).map((feature, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="feature-card glass rounded-2xl p-6 cursor-pointer"
                whileHover={{ y: -5 }}
                onClick={() => navigate(feature.link)}
                data-testid={`feature-card-${index}`}
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                  feature.color === 'cyan' 
                    ? 'bg-cyan-500/20 text-cyan-400' 
                    : feature.color === 'amber'
                    ? 'bg-amber-500/20 text-amber-400'
                    : feature.color === 'red'
                    ? 'bg-red-500/20 text-red-400'
                    : feature.color === 'pink'
                    ? 'bg-pink-500/20 text-pink-400'
                    : 'bg-slate-500/20 text-slate-400'
                }`}>
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold font-heading text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Compact Stats & CTA Strip */}
      <section className="py-6 px-6 lg:px-12">
        <div className="w-full">
          <div className="glass rounded-2xl p-4 lg:p-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              {/* 24/7 Badge */}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500/20 to-amber-500/20 flex items-center justify-center text-cyan-400">
                  <Clock className="w-5 h-5" />
                </div>
                <div>
                  <span className="text-xl font-bold font-heading gradient-text">24/7</span>
                  <span className="text-slate-400 text-sm ml-2">AI Tax Expert Available</span>
                </div>
              </div>
              
              {/* CTA */}
              <div className="flex items-center gap-3">
                <span className="text-slate-400 text-sm hidden sm:block">Ready to simplify your tax journey?</span>
                <Button 
                  className="btn-primary text-white font-medium px-6"
                  onClick={() => navigate('/chat')}
                  data-testid="cta-start-btn"
                >
                  Get Started for Free
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Subscribe Section - Full Width */}
      <NewsletterSubscribe />

      {/* Footer - Full Width */}
      <footer className="py-8 px-6 lg:px-12 border-t border-slate-800">
        <div className="w-full flex flex-col gap-6">
          {/* Top Row */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
              <img 
                src={TAXSETU_LOGO} 
                alt="TaxSetu Logo" 
                className="w-8 h-8 object-contain"
              />
              <span className="font-heading font-semibold text-white">TaxSetu AI</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-4 text-sm">
              <button 
                onClick={() => navigate('/pricing')} 
                className="text-slate-400 hover:text-cyan-400 transition-colors"
              >
                Pricing
              </button>
              <button 
                onClick={() => navigate('/privacy')} 
                className="text-slate-400 hover:text-cyan-400 transition-colors"
              >
                Privacy Policy
              </button>
              <button 
                onClick={() => navigate('/terms')} 
                className="text-slate-400 hover:text-cyan-400 transition-colors"
              >
                Terms
              </button>
              <button 
                onClick={() => navigate('/refund')} 
                className="text-slate-400 hover:text-cyan-400 transition-colors"
              >
                Refund Policy
              </button>
              <button 
                onClick={() => navigate('/contact')} 
                className="text-slate-400 hover:text-cyan-400 transition-colors"
              >
                Contact
              </button>
            </div>
          </div>
          {/* Bottom Row */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-2 pt-4 border-t border-slate-800/50">
            <p className="text-slate-500 text-xs text-center">
              Disclaimer: TaxSetu provides general tax information. Please consult a CA for specific advice.
            </p>
            <p className="text-slate-600 text-xs">© 2026 Credence Tax Advisors (OPC) Pvt. Ltd.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
